import React, { useEffect, useState, useRef } from 'react';

import {
  Form,
  Input,
  Button,
  Typography,
  Icon,
  getEncryptedData,
  setStoreValue,
  Tooltip,
  getStoreValue,
  checkEmpty,
  deleteStoreValue,
  useDeviceType,
} from 'pixel-react';
import { Link, useNavigate } from 'react-router-dom';

import {
  getWebSiteHostName,
  parseAndDecryptData,
} from '@/components/Header/utils';
import { CONFIG } from '@/lib/constants/config';
import { getProtocol } from '@/lib/utils/commonUtils.ts';
import { useUserSignoutMutation } from '@/store';
import { useSignInMutation } from '@/store/login/loginSlice';
import type { FormValues, FormErrors, FormHelperTexts } from '@/types/login';

import styles from './Login.module.scss';
import { useLazyGetAllResultConfigQuery } from '../../store/privilegeAI/privilegeai.ts';
import { SignUpWrapper } from '../SignUpContainer/SignUpWrapper';

export const LogIn = () => {
  const RSA_PUBLICK_Key = import.meta.env.VITE_RSA_PUBLIC_KEY;

  interface SignInResponse {
    userStatus: string;
    responseObject: {
      billingCycle: string;
      expires_in: string;
      refresh_token: string | object | null;
      access_token: string;
      currentLicenseId: string;
      version?: string;
    };
    responseCode: number;
    message: string;
  }

  const clientInfo = import.meta.env.VITE_CLIENT_DETAIL;
  const brandName = clientInfo?.name;

  const navigate = useNavigate();
  const initialFormState: FormValues = {
    email: '',
    password: '',
  };
  const [formData, setFormData] = useState<FormValues>(initialFormState);
  const [formErrors, setFormErrors] = useState<FormErrors>({
    email: false,
    password: false,
  });
  const [helperTexts, setHelperTexts] = useState<FormHelperTexts>({});

  const [isFormTouched, setIsFormTouched] = useState(false);
  const [isEmptyField, setIsEmptyField] = useState(true);

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [signIn, { isLoading }] = useSignInMutation();
  const [userSignout] = useUserSignoutMutation();
  const [getAiPrivileges] = useLazyGetAllResultConfigQuery();
  const { isMobile } = useDeviceType();
  const userDetails = getStoreValue(CONFIG.AUTH.USER_DETAILS) as {
    responseObject: {
      userStatus: string;
      userName: string;
      currentLicenseId: string;
    };
  };
  const { userStatus, userName, currentLicenseId } =
    userDetails?.responseObject ?? '';
  const search = window.location.search;
  const dataParam = new URLSearchParams(search).get('data');
  const isFromWebsite = new URLSearchParams(search).get('from');
  let aiPrivilegeTimeout: ReturnType<typeof setTimeout> | null = null;
  const signoutCalledRef = useRef(false);

  useEffect(() => {
    return () => {
      if (aiPrivilegeTimeout) clearTimeout(aiPrivilegeTimeout);
    };
  }, []);

  const validateField = (name: keyof FormValues, value: string) => {
    let hasError = false;
    let errorMessage = '';

    switch (name) {
      case 'email':
        hasError =
          value.trim() === '' ||
          !/^[a-zA-Z0-9]+(?:[._-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+(?:[._-][a-zA-Z0-9]+)+$/.test(
            value,
          );
        errorMessage =
          value.trim() === '' ? 'Email is required' : 'Invalid email address';
        break;
      case 'password':
        hasError = value.trim() === '';
        errorMessage = 'Password is required';
        break;
    }

    setFormErrors((prev: any) => ({ ...prev, [name]: hasError }));
    setHelperTexts((prev: any) => ({
      ...prev,
      [name]: hasError ? errorMessage : undefined,
    }));

    return hasError;
  };

  const triggerAiPrivilegeFetch = () => {
    if (aiPrivilegeTimeout) clearTimeout(aiPrivilegeTimeout);

    aiPrivilegeTimeout = setTimeout(async () => {
      try {
        const aiResponse = await getAiPrivileges().unwrap();
        if (aiResponse?.responseCode === 200) {
          setStoreValue('aiPrivilegeDetails', aiResponse.responseObject);
        } else {
          deleteStoreValue('aiPrivilegeDetails');
        }
      } catch (err) {
        console.error('AI Privilege fetch failed:', err);
        deleteStoreValue('aiPrivilegeDetails');
      }
    }, 500);
  };

  const handlePasswordChange = (value: string) => {
    setFormData(prev => ({ ...prev, password: value }));
    validateField('password', value);
  };

  const handleChange =
    (name: keyof FormValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFormData(prev => ({ ...prev, [name]: value }));
      validateField(name, value);
      setIsFormTouched(true);
      setIsEmptyField(false);
    };

  const handleSubmit = async () => {
    if (helperTexts.form) {
      //NOTE::  Prevent submission if there's a Email or Password is invalid error
      return;
    }

    // Reset error states before validating
    setFormErrors({
      email: false,
      password: false,
    });

    setHelperTexts({});
    const encryptedPassword = await getEncryptedData(
      formData.password,
      RSA_PUBLICK_Key,
      getProtocol(),
    );
    // Validate all fields and collect errors
    const errors: FormErrors = {
      email: validateField('email', formData.email),
      password: validateField('password', formData.password),
    };
    const hasErrors = Object.values(errors).some(error => error);

    if (hasErrors) {
      setIsFormTouched(true);
    }
    if (isEmptyField) return;
    // Update state with any validation errors
    setFormErrors(errors);

    // If there are no errors in the form fields and captcha is verified, proceed with submission
    if (!Object.values(errors).some(error => error)) {
      // TODO: Submit form data
    }
    setLoading(true);
    try {
      const payLoad = {
        emailId: formData.email,
        password: encryptedPassword,
        isSecureProtocol: getProtocol() === 'https' ? true : false,
      };

      const response = (await signIn(payLoad).unwrap()) as SignInResponse;
      if (response.responseCode === 200) {
        setStoreValue('test-optimize-user', response);
        if (
          response &&
          typeof response === 'object' &&
          'responseObject' in response
        ) {
          const responseObject = (
            response as unknown as { responseObject: { licenseType: string } }
          ).responseObject;
          setStoreValue('license-type', responseObject.licenseType || '');
        }
        setStoreValue(
          'current-project',
          JSON.stringify({ id: 'All Projects' }),
        );
        setStoreValue(
          CONFIG.AUTH.TOKEN_KEY,
          response.responseObject.access_token,
        );
        setStoreValue(
          CONFIG.AUTH.REFRESH_TOKEN_KEY,
          response.responseObject.refresh_token,
        );
        setStoreValue(
          CONFIG.AUTH.CURRENT_LICENSE_ID,
          response.responseObject.currentLicenseId,
        );
        if (
          Number(response?.responseObject?.expires_in) <=
          (response?.responseObject?.billingCycle === 'Yearly' ? 15 : 7)
        ) {
          navigate('/app/licenses');
        } else {
          navigate('/app/projects');
        }
        triggerAiPrivilegeFetch();
      } else if (response.responseCode === 400) {
        if (response.message.includes('Account Locked')) {
          navigate('/account-locked');
        } else if (response.message.includes('User is not activated')) {
          //TODO:This message 'User is not activated' should having some key from BE,once done change it
          navigate('/user-activation?email=' + formData.email);
        } else if (response.message.includes('Email or Password is invalid')) {
          //TODO:This message 'Email or Password is invalid' should having some key from BE,once done change it
          setHelperTexts(prev => ({ ...prev, form: response.message }));

          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Login failed. Please check your credentials.', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBlur = (name: keyof FormValues) => {
    validateField(name, formData[name]);
  };

  const hasErrors = () => {
    const hasFieldErrors = Object.values(formErrors).some(error => error);
    const areFieldsEmpty = !formData.email.trim() || !formData.password.trim();

    return hasFieldErrors || areFieldsEmpty;
  };

  const setLoginSessionData = () => {
    if (dataParam) {
      parseAndDecryptData(dataParam)
        .then(result => {
          if (result) {
            if (
              isFromWebsite &&
              (result?.userStatus === 'ACTIVE' ||
                checkEmpty(result?.expires_in))
            ) {
              setStoreValue(
                'test-optimize-user',
                JSON.stringify({ responseObject: result }),
              );
              setStoreValue('license-type', result?.licenseType || '');
              setStoreValue(CONFIG.AUTH.TOKEN_KEY, result?.access_token);
              setStoreValue('current-project', {
                id: 'All Projects',
                label: 'All Projects',
                value: 'All Projects',
                iconName: 'all_projects',
                type: '',
              });
              const targetRoute =
                Number(result?.expires_in) <=
                  (result?.billingCycle === 'Yearly' ? 15 : 7) ||
                checkEmpty(result?.expires_in)
                  ? '/app/licenses'
                  : '/app/projects';

              if (!checkEmpty(result?.version)) {
                document.cookie = `version=${result?.version}; path=/;`;
                window.location.href = targetRoute;
              } else {
                navigate(targetRoute);
              }
            }
          } else {
            console.error('Failed to decrypt data, result is null');
          }
        })
        .catch(error => {
          console.error('Error during decryption:', error);
        });
    }
  };
  const signOut = (pathname: string) => {
    if (signoutCalledRef.current) return;
    signoutCalledRef.current = true;

    const payload = {
      emailId: userName,
      currentLicenseId: currentLicenseId,
    };

    userSignout(payload)
      .then(response => {
        if (
          response?.data?.status === 'Success' &&
          import.meta?.env?.VITE_ENVIRONMENT !== 'onprem'
        ) {
          localStorage.clear();
          // window.location.replace(getWebSiteHostName() + pathname);
          window.location.replace(pathname);
        }
      })
      .catch((error: string) => {
        console.error('SignOut API Failed!', error);
        signoutCalledRef.current = false;
      });
  };
  const checkUserStatus = () => {
    if (userStatus === 'ACTIVE') {
      if (['/', '/signin'].includes(location?.pathname)) {
        signOut('/signin');
      } else if (location?.pathname === '/signup') {
        signOut('/signup');
      } else if (location?.pathname === '/forget-password') {
        signOut('/forgot-password');
      } else if (
        isFromWebsite ||
        [
          '/projectmenu/project',
          '/projectmenu/user',
          '/projectmenu/role',
        ].includes(location.pathname)
      ) {
        setStoreValue('current-project', {
          id: 'All Projects',
          label: 'All Projects',
          value: 'All Projects',
          iconName: 'all_projects',
          type: '',
        });
      }
    }
  };

  useEffect(() => {
    checkUserStatus();
  }, [location.pathname, userDetails]);
  useEffect(() => {
    setLoginSessionData();
  }, []);
  // commented for direct navigation to dev2
  // useEffect(() => {
  //   const currentHost = window.location.host;
  //   const targetHost = new URL(getWebSiteHostName()).host;
  //   const currentPath = window.location.pathname;
  //   const isAtSigninPage = currentPath === '/signin';
  //   const isSameHost = currentHost === targetHost;

  //   if (
  //     (['/', '/signin'].includes(currentPath) ||
  //       (checkEmpty(userDetails?.responseObject) && !isFromWebsite)) &&
  //     !(isAtSigninPage && isSameHost)
  //   ) {
  //     const newUrl = getWebSiteHostName() + '/signin';
  //     console.log('Redirecting to:', newUrl);
  //     window.location.replace(newUrl);
  //   }
  // }, []);

  return (
    <SignUpWrapper
      content_header={
        <>
          {' '}
          <Typography fontSize="25px" fontWeight="bold" lineHeight="0.8">
            SIMPLIFY TESTING. AMPLIFY QUALITY.
          </Typography>{' '}
        </>
      }
      sub_content={`${brandName} is your all-in-one testing solution - combining AI-driven intelligence with Super Automation to streamline end-to-end testing and supercharge your QA process.`}
      footer_content={`Get. Set. ${brandName}!`}
      copyright_content={`Copyright © ${brandName} Pvt Ltd. All Rights Reserved`}
      className={styles.login_form_container}
    >
      <section className={styles.form_container}>
        <aside className={styles.login_form}>
          <section className={styles.login_form_header}>
            <Typography
              fontSize="32px"
              lineHeight="26px"
              className={styles['header_text']}
            >
              Sign In
            </Typography>
          </section>
          <div>
            <Form<FormValues> onSubmit={handleSubmit} mode="onChange">
              {() => (
                <div>
                  <section className={`${styles.name_input} `}>
                    <Input
                      name="email"
                      label="Email"
                      placeholder="Enter your Email Id"
                      value={formData.email}
                      onChange={handleChange('email')}
                      onBlur={() => handleBlur('email')}
                      error={formErrors.email}
                      helperText={helperTexts.email}
                      variant="primary"
                      type="email"
                      autoComplete="on"
                      required
                      onFocus={() => {
                        setHelperTexts(prev => ({ ...prev, form: '' }));
                      }}
                      fixedLabel={true}
                    />
                  </section>

                  <section className={styles.cnf_password_input}>
                    <Input
                      name="password"
                      label="Password"
                      placeholder="Enter your Password"
                      value={formData.password}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        handlePasswordChange(e.target.value);
                        setIsEmptyField(false);
                        setHelperTexts(prev => ({ ...prev, form: '' }));
                      }}
                      onBlur={() =>
                        validateField('password', formData.password)
                      }
                      type={isPasswordVisible ? 'text' : 'password'}
                      error={formErrors.password}
                      iconProps={{
                        isShowIcon: true,
                      }}
                      helperText={helperTexts.password}
                      variant="primary"
                      autoComplete="on"
                      required
                      onFocus={() => {
                        setHelperTexts(prev => ({ ...prev, form: '' }));
                      }}
                      fixedLabel={true}
                    />
                  </section>

                  <article
                    onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                    className={styles.toggle_cnf_password_btn}
                  >
                    <Tooltip title={isPasswordVisible ? 'Hide' : 'Show'}>
                      <Icon
                        color="var(--brand-color)"
                        name={
                          isPasswordVisible
                            ? 'hide_access_icon'
                            : 'view_access_icon'
                        }
                      />
                    </Tooltip>
                  </article>
                  {helperTexts.form && (
                    <section className={styles.error_container}>
                      {' '}
                      <Typography
                        fontSize="10px"
                        color="var(--input-error-text-color)"
                      >
                        {' '}
                        {helperTexts.form}{' '}
                      </Typography>{' '}
                    </section>
                  )}
                  <section className={styles.forgot_pass_container}>
                    <Typography
                      fontSize={isMobile ? '12px' : '16px'}
                      className={styles['forgot_pass_text']}
                    >
                      <Link
                        to="/forgot-password"
                        className={styles['link-style']}
                      >
                        {' '}
                        Forgot Password ?
                      </Link>
                    </Typography>
                  </section>
                  <div className={styles.login_footerBtn}>
                    <Button
                      disabled={(isFormTouched && hasErrors()) || isLoading}
                      type="submit"
                      variant="primary"
                      buttonWidth="175px"
                    >
                      <Typography
                        fontSize="16px"
                        className={styles['register_btn']}
                      >
                        {loading &&
                        (!helperTexts.email || !helperTexts.password)
                          ? 'Signing In'
                          : 'Sign In'}
                      </Typography>
                    </Button>
                  </div>
                  {/* // commented the below code to hide create account, as for app, onprem image is syncing */}
                  {/* {import.meta?.env?.VITE_ENVIRONMENT === 'onprem' && (
                    <section className={styles.existing_acc}>
                      <Typography fontSize="16px">
                        Don’t have an account?{' '}
                        <Link className={styles.footer_highlight} to="/signup ">
                          {' '}
                          Create an Account
                        </Link>
                      </Typography>
                    </section>
                  )} */}
                </div>
              )}
            </Form>
          </div>
        </aside>
      </section>
    </SignUpWrapper>
  );
};
